import React, { useMemo, useState } from 'react'
import {
  MetricCard,
  SectionCard,
  SECTION_CARD_PREVIEW_STATE_LABELS,
  SECTION_CARD_PREVIEW_STATES,
  type SectionCardPreviewState,
  type SectionCardRadius,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SectionCardShowcase.css'

const SectionCardShowcase: React.FC = () => {
  const [title, setTitle] = useState('Collections')
  const [radius, setRadius] = useState<SectionCardRadius>('lg')
  const [showLink, setShowLink] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<SectionCardPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'section-card-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="section-card-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as SectionCardPreviewState,
                  )
                }
              >
                {SECTION_CARD_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SECTION_CARD_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'section-card-title',
            label: 'Title',
            control: (
              <input
                id="section-card-title"
                className="ui-field-control"
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            ),
          },
          {
            id: 'section-card-radius',
            label: 'Radius',
            control: (
              <select
                id="section-card-radius"
                className="ui-field-control"
                value={radius}
                onChange={(event) =>
                  setRadius(event.target.value as SectionCardRadius)
                }
              >
                <option value="lg">Large (16px) — dashboard</option>
                <option value="md">Medium (12px) — reports chart</option>
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'section-card-link',
            label: 'Title link',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="section-card-link"
                  type="checkbox"
                  checked={showLink}
                  onChange={(event) => setShowLink(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [highlightedState, radius, showLink, title],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Section card"
        description="Container shell for dashboard sections and report chart panels — title, optional link/actions, and a content body."
        stageClassName="ui-preview__stage--wide section-card-showcase__stage-wrap"
      >
        <div className={`section-card-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              The first section card reflects the highlighted state and property
              controls. Put charts, tables, or Metric cards in the body.
            </p>
            <div className="section-card-showcase__live-stack">
              <SectionCard
                title={title}
                titleHref={showLink ? '#collections' : undefined}
                radius={radius}
                contentLayout="row"
                previewState={highlightedState}
              >
                <MetricCard
                  label="Collected"
                  value="$18,420"
                  footer="8% vs last week"
                  trend="positive"
                />
                <MetricCard
                  label="Outstanding"
                  value="$6,110"
                  footer="3% vs last week"
                  trend="negative"
                />
                <MetricCard
                  label="Collection rate"
                  value="75%"
                  footer="On track"
                  trend="neutral"
                />
              </SectionCard>

              <SectionCard title="Production trend" radius="md">
                <div className="ds-section-card__body-placeholder">
                  Chart placeholder
                </div>
              </SectionCard>
            </div>
          </section>

          <section className="section-card-showcase__matrix">
            {SECTION_CARD_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`section-card-showcase__row${
                  previewState === highlightedState
                    ? ' section-card-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="section-card-showcase__label">
                  {SECTION_CARD_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <SectionCard previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Section card"
        sections={propertySections}
      />
    </>
  )
}

export default SectionCardShowcase
